import type { Role } from './roles.js'

/**
 * Types shared by the Express API and the React client.
 * Keep this file dependency-free — it is compiled by both tsconfigs.
 */

// --- Platform -------------------------------------------------------------------

export interface HealthResponse {
  status: 'ok' | 'degraded'
  version: string
  env: string
  uptimeSeconds: number
  database: {
    connected: boolean
    latencyMs: number | null
    migrationsApplied: number
    latestMigration: string | null
    error?: string
  }
}

export interface MetaResponse {
  appName: string
  clinicName: string
  env: string
  authEnabled: boolean
  semble: { enabled: boolean; sandbox: boolean; programmeLabel: string }
}

export interface ApiError {
  error: string
  message: string
}

// --- Auth ---------------------------------------------------------

export type TwoFactorMethod = 'none' | 'email' | 'totp'

export interface SessionResponse {
  authenticated: boolean
  role: Role | null
  username: string | null
  userId: number | null
  mustChangePassword: boolean
  mustEnrolTotp: boolean
  twoFactorPending: 'email' | 'totp' | null
  sharedPasswordEnabled: boolean
  mailEnabled: boolean
}

export interface TokenDescription {
  valid: boolean
  kind: 'invite' | 'reset'
  username: string | null
}

export interface SecurityResponse {
  username: string
  twoFactor: TwoFactorMethod
  totpEnrolled: boolean
  recoveryCodesLeft: number
  mailEnabled: boolean
}

export interface TotpSetup {
  qrDataUrl: string
  manualKey: string
  otpauthUrl: string
}

export interface RecoveryCodes {
  codes: string[]
}

export interface AppUser {
  id: number
  username: string
  displayName: string | null
  role: Role
  disabled: boolean
  mustChangePassword: boolean
  twoFactor: TwoFactorMethod
  totpEnrolled: boolean
  invitedAt: string | null
  createdAt: string
  createdBy: string | null
  lastLoginAt: string | null
}

export interface UsersResponse {
  users: AppUser[]
  mailEnabled: boolean
}

export interface UserLink {
  user: AppUser
  kind: 'invite' | 'reset'
  link: string
  expiresAt: string
  emailed: boolean
  reason: string | null
}

export interface AuditEntry {
  id: number
  at: string
  username: string
  role: string | null
  action: string
  target: string | null
  detail: Record<string, unknown>
  method: string
  path: string
  status: number
}

export interface AuditResponse {
  entries: AuditEntry[]
  nextBefore: number | null
}

// --- Patients ----------------------------------------------------------------------

export type Sex = 'female' | 'male' | 'unknown'

export type AssessmentStatus = 'draft' | 'in_review' | 'signed' | 'delivered'

export interface AssessmentSummary {
  id: number
  patientId: number
  programme: string
  assessmentDate: string
  status: AssessmentStatus
  createdAt: string
  createdBy: string | null
  signedAt: string | null
  signedBy: string | null
  latestVersion: number | null
  resultCount: number
  reportType: ReportType
}

/** 'full' is the Prevent51 report; 'summary' is the short results letter with chosen sections. */
export type ReportType = 'full' | 'summary'

export interface ReportOptions {
  type: ReportType
  /** Section codes chosen for a summary report. Null = every section with a result. */
  sections: string[] | null
  overallComment: string
}

// --- Trends --------------------------------------------------------------------------

export interface TrendPoint {
  assessmentId: number
  date: string
  value: number
  valueText: string
  bandLabel: string | null
  bandColour: BandColour | null
}

export interface TrendSeries {
  code: string
  name: string
  unit: string
  decimals: number
  sectionCode: string
  isKey: boolean
  keyOrder: number
  bands: Band[]
  points: TrendPoint[]
}

export interface PatientTrends {
  patient: Omit<PatientSummary, 'assessments'>
  assessments: AssessmentSummary[]
  sections: SectionDef[]
  series: TrendSeries[]
}

export interface PatientSummary {
  id: number
  sembleId: string | null
  firstName: string
  lastName: string
  dob: string | null
  sex: Sex
  email: string | null
  lastSyncedAt: string | null
  assessments: AssessmentSummary[]
}

/** A hit from a live Semble search, with the local record if one exists. */
export interface SemblePatientHit {
  sembleId: string
  firstName: string
  lastName: string
  dob: string | null
  email: string | null
  labels: string[]
  localId: number | null
}

// --- Reference data ----------------------------------------------------------------

export type BandColour = 'teal' | 'green' | 'amber' | 'red' | 'grey'

export interface Band {
  label: string
  colour: BandColour
  /** Null = open-ended. Lower bound is inclusive, upper exclusive, unless flagged. */
  lower: number | null
  upper: number | null
  lowerInclusive: boolean
  upperInclusive: boolean
  isTarget: boolean
  /** The words shown next to the range in the report, e.g. "Optimal <0.9". */
  display: string
}

/** One band row as stored, for the Reference ranges editor. */
export interface RangeRowView {
  id: number
  markerCode: string
  sex: 'any' | 'female' | 'male'
  ageMin: number | null
  ageMax: number | null
  sort: number
  label: string
  display: string
  colour: BandColour
  lower: number | null
  upper: number | null
  lowerInclusive: boolean
  upperInclusive: boolean
  isTarget: boolean
  confirmed: boolean
  note: string | null
}

export interface MarkerDef {
  code: string
  name: string
  shortName: string | null
  unit: string
  valueKind: 'numeric' | 'text'
  sectionCode: string
  displayOrder: number
  decimals: number
  isDerived: boolean
  patientCopy: string | null
  isKey?: boolean
}

export interface SectionDef {
  code: string
  title: string
  /** Sub-sections of "Current health biomarkers" carry the parent title. */
  parentTitle: string | null
  displayOrder: number
  backgroundCopy: string
  /** Template for the per-section comment heading, e.g. "Comments on your lipids". */
  commentHeading: string
}

// --- Assessment detail -------------------------------------------------------------

export type SourceKind = 'semble_lab' | 'semble_consultation' | 'semble_questionnaire' | 'glycanage_pdf' | 'dexa_pdf' | 'manual' | 'derived'

export interface ResultView {
  id: number | null
  markerCode: string
  name: string
  shortName: string | null
  unit: string
  sectionCode: string
  valueKind: 'numeric' | 'text'
  decimals: number
  value: number | null
  valueText: string | null
  measuredOn: string | null
  /** Band the value lands in against Clinic51's ranges, or the clinician's override. */
  band: { label: string; colour: BandColour; overridden: boolean } | null
  bands: Band[]
  labRangeText: string | null
  labFlag: string | null
  isDerived: boolean
  source: { kind: SourceKind; label: string; locator: string | null } | null
  enteredBy: string | null
  note: string | null
}

export interface SectionComment {
  sectionCode: string
  comment: string
  aiDrafted: boolean
  updatedBy: string | null
  updatedAt: string | null
}

export type LifestyleArea = 'movement' | 'nutrition' | 'sleep' | 'emotional' | 'purpose' | 'harmful'
export type LifestyleStatus = 'good' | 'fair' | 'poor'

export interface LifestyleRating {
  area: LifestyleArea
  status: LifestyleStatus | null
  comment: string
}

export const LIFESTYLE_AREAS: ReadonlyArray<{ area: LifestyleArea; label: string }> = [
  { area: 'movement', label: 'Movement' },
  { area: 'nutrition', label: 'Nutrition' },
  { area: 'sleep', label: 'Sleep and recovery' },
  { area: 'emotional', label: 'Emotional wellbeing' },
  { area: 'purpose', label: 'Purpose and connection' },
  { area: 'harmful', label: 'Harmful substances' },
]

export interface ScreeningRow {
  id: number
  cancer: string
  increasedRisk: boolean | null
  screenName: string
  lastScreen: string
  result: string
}

export interface ExceptionRow {
  id: number
  severity: 'info' | 'warning' | 'error'
  code: string
  message: string
  raisedAt: string
  resolvedAt: string | null
}

export interface SourceDocumentInfo {
  id: number
  kind: SourceKind
  label: string
  sembleRef: string | null
  fileName: string | null
  byteCount: number | null
  uploadedAt: string
  uploadedBy: string | null
  extractionStatus: 'pending' | 'extracted' | 'manual' | 'failed'
  notes: string | null
}

export interface ReportVersionInfo {
  id: number
  version: number
  generatedAt: string
  generatedBy: string | null
  pdfBytes: number
  sembleDocumentId: string | null
  sembleDocumentTitle: string | null
  pushedAt: string | null
}

export interface AssessmentNarrative {
  keyFindings: string
  visionStatement: string
  geneticsText: string
  lifestyleIntro: string
  clinicianName: string
  clinicianTitle: string
  clinicianGmc: string
  intro: string
}

export interface AssessmentDetail {
  assessment: AssessmentSummary
  patient: Omit<PatientSummary, 'assessments'>
  narrative: AssessmentNarrative
  reportOptions: ReportOptions
  sections: SectionDef[]
  results: ResultView[]
  comments: SectionComment[]
  lifestyle: LifestyleRating[]
  screening: ScreeningRow[]
  exceptions: ExceptionRow[]
  sources: SourceDocumentInfo[]
  versions: ReportVersionInfo[]
}

// --- Report model: the frozen snapshot the renderer draws ---------------------------

export interface ReportMarkerRow {
  code: string
  name: string
  unit: string
  valueText: string
  bandLabel: string | null
  bandColour: BandColour | null
  bands: Band[]
  labRangeText: string | null
  isDerived: boolean
  /** Zero-to-one position of the value along the band strip, for the marker pin. */
  position: number | null
  /** The same marker at the patient's previous screen, when there was one. */
  previous?: { date: string; value: number; valueText: string } | null
}

export interface ReportSection {
  code: string
  title: string
  parentTitle: string | null
  backgroundCopy: string
  markers: ReportMarkerRow[]
  commentHeading: string
  comment: string
}

export interface ReportModel {
  clinic: {
    name: string
    programme: string
    address: string
    phone: string
    email: string
    web: string
    companyNo: string
    regulator: string
  }
  patient: { firstName: string; lastName: string; dob: string | null; sex: Sex; sembleId: string | null }
  assessment: { date: string; id: number; version: number }
  narrative: AssessmentNarrative
  keyFindings: string[]
  lifestyle: Array<{ label: string; status: LifestyleStatus | null; comment: string }>
  screening: ScreeningRow[]
  sections: ReportSection[]
  generatedAt: string
  reportType: ReportType
  /** Printed in the header band. */
  title: string
  overallComment: string
  /** Key markers with at least two screens, for the trend charts. */
  trends: TrendSeries[]
}

export interface TrendsReportModel {
  clinic: ReportModel['clinic']
  title: string
  patient: ReportModel['patient']
  generatedAt: string
  sections: SectionDef[]
  series: TrendSeries[]
}

// --- Semble pull ------------------------------------------------------------------

export interface PullResult {
  labsFound: number
  labsMapped: number
  observationsFound: number
  observationsMapped: number
  exceptionsRaised: number
  notes: string[]
}
